import React from 'react';

const setup = [
  {
    t: 'Models',
    items: ['LLaMA-2-7B-Chat', 'LLaMA-2-13B-Chat', 'Mistral-7B-Instruct-0.1'],
    tone: 'text-brand-700',
  },
  {
    t: 'Attacks',
    items: ['BadNets', 'VPI', 'Sleeper Agents', 'MTBA', 'CTBA'],
    tone: 'text-accent-600',
  },
  {
    t: 'Tasks',
    items: ['Sentiment Steering', 'Targeted Refusal', 'Code Injection (apx.)'],
    tone: 'text-emerald-700',
  },
  {
    t: 'Threat Models',
    items: ['Full-parameter SFT', 'LoRA adapter-only'],
    tone: 'text-brand-700',
  },
];

const headlines = [
  { v: '93% → <1%', l: 'BadNets ASR drop (Sentiment Steering)' },
  { v: '100% → 16%', l: 'BadNets ASR drop (Targeted Refusal, LoRA, 13B)' },
  { v: '~0% loss', l: 'utility on 10 benchmarks (LoRA setting)' },
  { v: '5+ attacks', l: 'withstood under both threat models' },
];

export default function Experiment() {
  return (
    <section className="py-20 bg-white border-t border-ink-100">
      <div className="max-w-6xl mx-auto px-6">
        <p className="section-eyebrow mb-3">05 &middot; Does it actually work?</p>
        <h2 className="section-title">Experiments</h2>

        <p className="mt-6 text-center text-ink-500 max-w-3xl mx-auto leading-relaxed">
          We stress-test BD-VAX across two model sizes, two backdoor tasks,
          two threat models, and five representative attacks &mdash; without
          ever revealing the real trigger or providing a clean reference.
        </p>

        {/* Setup grid */}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {setup.map((g) => (
            <div key={g.t} className="card p-5">
              <div
                className={`text-[10px] uppercase tracking-widest font-semibold ${g.tone}`}
              >
                {g.t}
              </div>
              <ul className="mt-3 space-y-1.5 text-sm text-ink-700">
                {g.items.map((it) => (
                  <li key={it} className="flex gap-2">
                    <span className="text-ink-400">·</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Headline numbers */}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {headlines.map((s) => (
            <div
              key={s.l}
              className="card p-5 hover:shadow-card hover:-translate-y-0.5 transition-all"
            >
              <div className="text-2xl font-bold text-brand-700 font-serif">
                {s.v}
              </div>
              <div className="mt-1 text-xs sm:text-sm text-ink-500">
                {s.l}
              </div>
            </div>
          ))}
        </div>

        {/* Result 1 */}
        <h3 className="subsection-title text-center">
          Result 1 &mdash; Backdoor purification across attacks
        </h3>
        <p className="text-center text-ink-500 max-w-3xl mx-auto leading-relaxed">
          Attack Success Rate (lower is better) on LLaMA-2-{'{7B,13B}'}-Chat
          across five attacks and two threat models. BD-VAX outperforms
          FT&nbsp;/&nbsp;Pruning&nbsp;/&nbsp;Quantization&nbsp;/&nbsp;CROW&nbsp;/&nbsp;Fine-Pruning
          baselines by a wide margin.
        </p>

        <figure className="mt-6 card p-4 md:p-5">
          <div className="rounded-xl overflow-hidden bg-white">
            <img
              src={`${process.env.PUBLIC_URL}/figures/asr_results.jpg`}
              alt="Table 2: ASR of BD-VAX vs. baseline defenses across BadNets, VPI, Sleeper, MTBA, CTBA on LLaMA-2-7B-Chat and LLaMA-2-13B-Chat."
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
          <figcaption className="figure-caption">
            Table 2. Backdoor performance. ASR (lower is better) across two
            LLMs, two tasks (Sentiment Steering, Targeted Refusal), and two
            threat models (full-model, adapter-only). Attacks: BadNets, VPI,
            Sleeper, MTBA, CTBA.
          </figcaption>
        </figure>

        {/* Result 2 */}
        <h3 className="subsection-title text-center">
          Result 2 &mdash; How many variants do we need?
        </h3>

        <div className="mt-4 grid lg:grid-cols-2 gap-6 items-start">
          <div className="card p-6 md:p-7">
            <p className="text-ink-700 leading-relaxed text-[0.98rem]">
              ASR drops quickly as the number of synthetic variants N grows.
              By N&nbsp;=&nbsp;5&ndash;7, the signature is sharp enough that
              purification has converged on all three settings. We use
              N&nbsp;=&nbsp;7 by default.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm text-ink-600">
              <li className="flex gap-2">
                <span className="text-brand-500 mt-0.5">●</span>
                Sentiment Steering is the easiest to scrub: ASR is already low
                at N=2.
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500 mt-0.5">▲</span>
                13B model traces 7B closely &mdash; the recipe generalizes
                across scale.
              </li>
              <li className="flex gap-2">
                <span className="text-amber-500 mt-0.5">■</span>
                Targeted Refusal is the hardest backdoor; still drops &gt;75%
                with N=7.
              </li>
            </ul>
          </div>
          <figure className="card p-4 md:p-5">
            <div className="rounded-xl overflow-hidden bg-white">
              <img
                src={`${process.env.PUBLIC_URL}/figures/ablation_n.jpg`}
                alt="Figure 2: Effect of number of backdoor variants N on purification performance."
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <figcaption className="figure-caption">
              Figure 2. Effect of N on purification (ASR, lower is better) for
              BadNets on LLaMA-2-7B/13B-Chat across Sentiment Steering and
              Targeted Refusal.
            </figcaption>
          </figure>
        </div>

        {/* Utility note */}
        <div className="mt-12 max-w-3xl mx-auto">
          <div className="relative rounded-2xl bg-grad-callout p-[1px] shadow-card">
            <div className="rounded-2xl bg-white p-6 md:p-7">
              <div className="flex items-center gap-2 text-brand-700 font-semibold text-sm">
                <span aria-hidden="true">↗</span> Utility is preserved
              </div>
              <p className="mt-3 text-ink-700 leading-relaxed text-[0.98rem]">
                Across ten close-ended benchmarks (OpenBookQA, HellaSwag,
                WinoGrande, ARC, BoolQ, PIQA, GSM8K, MMLU…) and MT-Bench,
                BD-VAX's purified models track the clean baseline closely. On{' '}
                <strong>LoRA</strong>, average accuracy on LLaMA-2-7B-Chat is{' '}
                <strong>66.54%</strong> vs.{' '}
                <strong>66.30%</strong> for the clean model &mdash;
                statistically indistinguishable, while ASR is driven to near
                zero.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
